/**
 * Cart Storage Service
 * Handles local storage and cookie management for cart data
 * Extracted from the main cart context for better separation of concerns
 */

import Cookies from 'js-cookie';
import type { CartItem } from '../../app/hiv-test/types';
import type { ShopifyCart } from './ShopifyCartService';

export class CartStorageService {
  private static readonly CART_COOKIE_KEY = 'cart';
  private static readonly SHOPIFY_CART_ID_KEY = 'shopifyCartId';
  private static readonly SHOPIFY_CHECKOUT_URL_KEY = 'shopifyCheckoutUrl';
  private static readonly COOKIE_EXPIRY_DAYS = 7;

  /**
   * Saves cart data to cookies
   */
  saveCartToCookies(cart: CartItem[]): void {
    if (cart.length > 0) {
      Cookies.set(CartStorageService.CART_COOKIE_KEY, JSON.stringify(cart), {
        expires: CartStorageService.COOKIE_EXPIRY_DAYS,
      });
    } else {
      Cookies.remove(CartStorageService.CART_COOKIE_KEY);
    }
  }

  /**
   * Loads cart data from cookies
   */
  loadCartFromCookies(): CartItem[] {
    const savedCart = Cookies.get(CartStorageService.CART_COOKIE_KEY);
    if (savedCart) {
      try {
        return JSON.parse(savedCart);
      } catch (error) {
        console.error('Error parsing saved cart:', error);
        return [];
      }
    }
    return [];
  }

  /**
   * Saves Shopify cart data to cookies
   */
  saveShopifyCartToCookies(shopifyCart: ShopifyCart): void {
    if (shopifyCart.id) {
      Cookies.set(CartStorageService.SHOPIFY_CART_ID_KEY, shopifyCart.id, {
        expires: CartStorageService.COOKIE_EXPIRY_DAYS,
      });

      if (shopifyCart.checkoutUrl) {
        Cookies.set(
          CartStorageService.SHOPIFY_CHECKOUT_URL_KEY,
          shopifyCart.checkoutUrl,
          {
            expires: CartStorageService.COOKIE_EXPIRY_DAYS,
          }
        );
      } else {
        console.error('New cart missing checkoutUrl:', shopifyCart);
      }
    }
  }

  /**
   * Loads Shopify cart data from cookies
   */
  loadShopifyCartFromCookies(): ShopifyCart | null {
    const savedShopifyCartId = Cookies.get(
      CartStorageService.SHOPIFY_CART_ID_KEY
    );
    const savedShopifyCheckoutUrl = Cookies.get(
      CartStorageService.SHOPIFY_CHECKOUT_URL_KEY
    );

    if (savedShopifyCartId) {
      return {
        checkoutUrl: savedShopifyCheckoutUrl || null,
        id: savedShopifyCartId,
      };
    }
    return null;
  }

  /**
   * Clears all cart-related cookies
   */
  clearAllCartCookies(): void {
    Cookies.remove(CartStorageService.CART_COOKIE_KEY);
    Cookies.remove(CartStorageService.SHOPIFY_CART_ID_KEY);
    Cookies.remove(CartStorageService.SHOPIFY_CHECKOUT_URL_KEY);
  }

  /**
   * Clears only Shopify-related cookies
   */
  clearShopifyCartCookies(): void {
    Cookies.remove(CartStorageService.SHOPIFY_CART_ID_KEY);
    Cookies.remove(CartStorageService.SHOPIFY_CHECKOUT_URL_KEY);
  }
}
